import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FiLoader } from "react-icons/fi";
import toast, { Toaster } from "react-hot-toast";
import {
  Mail,
  Lock,
  Sparkles,
  Shield,
  ArrowLeft,
  CheckCircle,
  Eye,
  EyeOff,
  Key,
} from "lucide-react";

function ResetPassword() {
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState([]);

  const navigate = useNavigate();
  const location = useLocation();

  // Optional: pre-fill email from query params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const emailParam = params.get("email");
    if (emailParam) setEmail(emailParam);
  }, [location]);

  const validatePasswordStrength = (password) => {
    const rules = [
      { regex: /.{8,}/, message: "At least 8 characters" },
      { regex: /[A-Z]/, message: "At least one uppercase letter" },
      { regex: /[a-z]/, message: "At least one lowercase letter" },
      { regex: /[0-9]/, message: "At least one number" },
      { regex: /[^A-Za-z0-9]/, message: "At least one special character" },
    ];
    const failedRules = rules
      .filter((rule) => !rule.regex.test(password))
      .map((rule) => rule.message);
    setPasswordErrors(failedRules);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match.", {
        duration: 4000,
        style: {
          borderRadius: '12px',
          background: '#ef4444',
          color: '#fff',
        },
      });
      setLoading(false);
      return;
    }

    if (passwordErrors.length > 0) {
      toast.error("Please ensure your password meets all requirements.", {
        duration: 4000,
        style: {
          borderRadius: '12px',
          background: '#ef4444',
          color: '#fff',
        },
      });
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(
        "https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/email/reset-password",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, newPassword, confirmPassword }),
        }
      );

      if (response.ok) {
        toast.success("Password has been reset successfully!", {
          duration: 3000,
          style: {
            borderRadius: '12px',
            background: '#10b981',
            color: '#fff',
          },
          iconTheme: {
            primary: '#fff',
            secondary: '#10b981',
          },
        });
        setEmail("");
        setNewPassword("");
        setConfirmPassword("");

        // Redirect to login after 2 seconds
        setTimeout(() => navigate("/login"), 2000);
      } else {
        let errorMessage = "Failed to reset password";
        try {
          const data = await response.json();
          errorMessage = data.message || errorMessage;
        } catch {
          const text = await response.text();
          if (text) errorMessage = text;
        }
        toast.error(errorMessage, {
          duration: 4000,
          style: {
            borderRadius: '12px',
            background: '#ef4444',
            color: '#fff',
          },
        });
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again.", {
        duration: 4000,
        style: {
          borderRadius: '12px',
          background: '#ef4444',
          color: '#fff',
        },
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 sm:px-6 py-8 relative"
      style={{
        backgroundImage:
          "url('https://github.com/Giftthane551/Hotel-Front-End/raw/main/MSABackground.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      {/* Overlay for better readability */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />

      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            borderRadius: '12px',
            background: '#fff',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
          },
        }}
      />

      <div className="relative z-10 w-full max-w-md">
        {/* Decorative elements */}
        <div className="absolute -top-20 -left-20 w-64 h-64 bg-gradient-to-r from-blue-400/20 to-purple-400/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-20 -right-20 w-64 h-64 bg-gradient-to-r from-pink-400/20 to-purple-400/20 rounded-full blur-3xl" />

        {/* Back to Login Button */}
        <Link
          to="/login"
          className="inline-flex items-center gap-2 mb-4 text-white/80 hover:text-white transition-colors duration-200 group"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          <span className="text-sm font-medium">Back to Login</span>
        </Link>

        {/* Main Card */}
        <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl border border-gray-200 p-6 sm:p-8 transform transition-all duration-300 hover:shadow-3xl">
          {/* Brand */}
          <div className="flex flex-col items-center mb-8">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full blur-lg opacity-50 animate-pulse" />
              <div className="relative w-20 h-20 bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 rounded-full flex items-center justify-center shadow-lg transform transition-transform hover:scale-105 duration-300">
                <Key className="w-8 h-8 text-white animate-pulse" />
              </div>
            </div>
            <h1 className="mt-5 text-2xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
              Reset Password
            </h1>
            <p className="text-sm text-gray-500 mt-2 text-center">
              Create a new secure password for your account
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="student@msa.edu"
                  required
                  className="
                    w-full pl-10 pr-4 py-3 text-sm
                    border-2 border-gray-200 rounded-xl
                    text-gray-800 placeholder:text-gray-400
                    focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20
                    transition-all duration-200
                  "
                />
              </div>
            </div>

            {/* New Password */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                New Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <input
                  type={showNewPassword ? "text" : "password"}
                  id="newPassword"
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    validatePasswordStrength(e.target.value);
                  }}
                  placeholder="Enter new password"
                  required
                  className="
                    w-full pl-10 pr-12 py-3 text-sm
                    border-2 border-gray-200 rounded-xl
                    text-gray-800 placeholder:text-gray-400
                    focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20
                    transition-all duration-200
                  "
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-3 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {newPassword && (
                <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
                  <p className="text-xs font-semibold text-blue-900 mb-2">Password requirements:</p>
                  <div className="space-y-1">
                    {passwordErrors.length > 0 ? (
                      passwordErrors.map((err, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-xs text-red-600">
                          <div className="w-1 h-1 bg-red-600 rounded-full" />
                          <span>{err}</span>
                        </div>
                      ))
                    ) : (
                      <div className="flex items-center gap-2 text-xs text-green-600">
                        <CheckCircle className="w-3 h-3" />
                        <span>Password meets all requirements</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  required
                  className="
                    w-full pl-10 pr-12 py-3 text-sm
                    border-2 border-gray-200 rounded-xl
                    text-gray-800 placeholder:text-gray-400
                    focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20
                    transition-all duration-200
                  "
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-3 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Password Match Indicator */}
            {confirmPassword && newPassword && (
              <div className="flex items-center gap-2">
                {newPassword === confirmPassword ? (
                  <div className="flex items-center gap-2 text-xs text-green-600">
                    <CheckCircle className="w-3 h-3" />
                    <span>Passwords match</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-xs text-red-600">
                    <div className="w-1 h-1 bg-red-600 rounded-full" />
                    <span>Passwords do not match</span>
                  </div>
                )}
              </div>
            )}

            {/* Reset Button */}
            <button
              type="submit"
              disabled={loading}
              className={`
                w-full flex items-center justify-center gap-2
                px-4 py-3 rounded-xl text-sm font-semibold
                transition-all duration-200 transform hover:scale-105
                ${loading
                  ? "bg-gradient-to-r from-blue-400 to-purple-400 cursor-not-allowed"
                  : "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow-md hover:shadow-lg"}
                text-white
              `}
            >
              {loading ? (
                <>
                  <FiLoader className="animate-spin w-4 h-4" />
                  <span>Resetting Password...</span>
                </>
              ) : (
                <>
                  <Key className="w-4 h-4" />
                  <span>Reset Password</span>
                </>
              )}
            </button>

            {/* Helpful Tips */}
            <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-100">
              <div className="flex items-start gap-3">
                <div className="p-1.5 bg-blue-100 rounded-lg">
                  <Sparkles className="w-4 h-4 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h4 className="text-xs font-semibold text-blue-900 mb-1">
                    Password Tips:
                  </h4>
                  <ul className="text-xs text-blue-700 space-y-1">
                    <li>• Use at least 8 characters</li>
                    <li>• Include uppercase and lowercase letters</li>
                    <li>• Add numbers and special characters</li>
                    <li>• Avoid using common words or personal info</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Login Link */}
            <div className="text-center">
              <p className="text-sm text-gray-600">
                Remembered your password?{" "}
                <Link 
                  to="/login" 
                  className="text-blue-600 hover:text-blue-700 font-semibold hover:underline transition-colors"
                >
                  Back to Login
                </Link>
              </p>
            </div>
          </form>
        </div>

        {/* Security Notice */}
        <div className="mt-6 text-center">
          <div className="flex items-center justify-center gap-2 text-xs text-white/80">
            <Shield className="w-4 h-4" />
            <span>Secure password reset with 256-bit encryption</span>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-xs text-white/60">
            © 2024 MSA Student Portal. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;